import React, { useEffect, useState } from 'react';
import { COMPUTE_PERFORMANCE_BASED_BONUS, EXPENSE_TYPES, useExpenseService } from 'domain/shared/expense';
import { ButtonWithConfirmation } from 'framework/ui/ui-builder';
import { useListContext } from 'framework/list';
import { DATE_FORMAT, useDateService } from 'framework/utils';
import { DateInput, FormFieldsData } from 'framework/form';
import moment from 'moment';

const transformDate = (source: string): string => {
  return source
    ? moment(source, DATE_FORMAT.API_ONLY_MONTH).add(1, 'month').subtract(1, 'day').format(DATE_FORMAT.API_ONLY_DATE)
    : source;
};

const ComputeEmployeeAccrualsButton = ({ disabledInput = false, initialValue = '', ...props }) => {
  const { computeExpenses } = useExpenseService();
  const { reloadData } = useListContext();
  const [dateErrors, setDateErrors] = useState<string[]>([]);
  const { getToday } = useDateService();
  const maxDate = getToday().date(1).subtract(1, 'day');
  const [date, setDate] = useState(transformDate(initialValue) || maxDate);

  const onDateChange = (values: FormFieldsData) => {
    setDate(values.date);
    const isValid = moment(values.date, DATE_FORMAT.API_ONLY_DATE, true).isValid();

    if (!values.date) {
      setDateErrors(['Field is required']);
    } else if (!isValid) {
      setDateErrors(['Please, choose the correct date']);
    } else if (moment(values.date, DATE_FORMAT.API_ONLY_DATE, true).isAfter(maxDate)) {
      setDateErrors(['Please, choose date earlier than current month']);
    } else {
      setDateErrors([]);
    }
  };

  useEffect(() => {
    if (initialValue) {
      onDateChange({ date: transformDate(initialValue) });
    }
    // Suppressed warnings because we only need to call useEffect callback if currentFilterValue is changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue]);

  return (
    <ButtonWithConfirmation
      modalChildren={
        <DateInput
          source="date"
          value={date as any}
          label="Compute Date"
          onChangeCallback={onDateChange}
          errorMessages={dateErrors}
          isRequired
          dateFormat={DATE_FORMAT.API_ONLY_DATE}
          maxDate={maxDate}
          disabled={disabledInput}
          sx={{ mt: 2.5 }}
        />
      }
      submitMethod={() =>
        computeExpenses(moment(date).format(DATE_FORMAT.API_ONLY_DATE), EXPENSE_TYPES.EMPLOYEE_EXPENSE_ACCRUAL)
      }
      variant="outlined"
      confirmBtnProps={{ disabled: !!dateErrors.length }}
      displayMessage="Do you really want to compute expenses accruals? Please fill out the inputs below."
      afterSubmit={{
        successMessage: 'The expenses accruals have been successfully computed.',
        errorMessage: "Can't compute accruals.",
        execute: () => {
          if (reloadData) {
            reloadData();
          }
        },
      }}
      inputLabel="Compute Date"
      permissionToCheck={COMPUTE_PERFORMANCE_BASED_BONUS}
      {...props}
    >
      Compute expenses accruals
    </ButtonWithConfirmation>
  );
};

export default ComputeEmployeeAccrualsButton;
